import type React from "react"
import { useId, useState } from "react"
import { useTranslation } from "react-i18next"

import { useFormatDecimal } from "@/formatters"
import type { Meter } from "@/models/meter"
import type { Organization } from "@/models/organization"
import EmptyChart from "@/modules/site/components/site/emptyChart"
import { GraphMenuContainer } from "@/nzds/graphs/graph-menu/graph-menu-container"
import { StackedBarGraph } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph"
import type { StackedBarGraphSeries } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph.types"
import { HStack } from "@/nzds/layout"
import { useAllBillPeriods } from "@/services/bill-period/use-all-bill-periods"
import type { BillPeriod } from "@/services/core-api/types"

import { Box, FormLabel, Skeleton, Stack } from "@mui/material"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"

import { SiteBillsMonthlyChartTooltip } from "./site-bills-monthly-chart-tooltip"
import {
  type SiteBillsChartBill,
  type SiteBillsChartMeasure,
  buildSiteBillsMonthlyChart,
} from "./site-bills-monthly-series"
import {
  formatBillMonthLabel,
  startOfMonthOneYearBefore,
} from "./site-bills-range"

const CHART_HEIGHT = 360

const SITE_BILLS_CHART_MEASURES = ["usage", "cost"] as const

interface SiteBillsMonthlyChartProps {
  end: string
  meterId: string | null
  meters: readonly Meter[]
  organizationId: Organization["id"]
  resourceType: string
  siteId: number
  start: string
}

const toChartBill = (
  period: BillPeriod,
  meterCodeById: ReadonlyMap<string, string>
): SiteBillsChartBill => ({
  cost: period.cost_major ?? null,
  currency: period.currency,
  endDate: period.end_date,
  id: String(period.id),
  meterCode:
    meterCodeById.get(String(period.meter_id)) ?? String(period.meter_id),
  meterId: period.meter_id,
  startDate: period.start_date,
  usage: period.usage_value,
  usageUnit: period.usage_unit,
})

const resolveSelectedSeriesKey = (
  isLoading: boolean,
  selectedSeriesKey: string | null,
  series: readonly StackedBarGraphSeries[]
): string | null => {
  if (selectedSeriesKey == null || isLoading) {
    return selectedSeriesKey
  }

  return series.some((item) => item.key === selectedSeriesKey)
    ? selectedSeriesKey
    : null
}

const isChartMeasure = (value: string | null): value is SiteBillsChartMeasure =>
  value === "usage" || value === "cost"

const SiteBillsChartMeasureToggle: React.FC<{
  measure: SiteBillsChartMeasure
  onMeasureChange: (measure: SiteBillsChartMeasure) => void
}> = ({ measure, onMeasureChange }) => {
  const { t } = useTranslation("sites")
  const labelId = useId()

  return (
    <Stack spacing={0.5}>
      <FormLabel id={labelId} sx={{ typography: "caption" }}>
        {t("bills.chart.show")}
      </FormLabel>
      <ToggleButtonGroup
        aria-labelledby={labelId}
        exclusive
        onChange={(_event, next: string | null) => {
          if (isChartMeasure(next)) {
            onMeasureChange(next)
          }
        }}
        size="small"
        value={measure}
      >
        {SITE_BILLS_CHART_MEASURES.map((item) => (
          <ToggleButton key={item} value={item}>
            {t(`bills.chart.${item}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )
}

export const SiteBillsMonthlyChart: React.FC<SiteBillsMonthlyChartProps> = ({
  end,
  meterId,
  meters,
  organizationId,
  resourceType,
  siteId,
  start,
}) => {
  const { i18n, t } = useTranslation("sites")
  const { formatDecimal } = useFormatDecimal(
    {
      compactDisplay: "short",
      // One fraction digit so a tick of 1,500 stays 1.5K.
      maximumFractionDigits: 1,
      notation: "compact",
    },
    { useAccountingFormatWhenNegative: false }
  )
  const [measure, setMeasure] = useState<SiteBillsChartMeasure>("usage")
  const [selectedSeriesKey, setSelectedSeriesKey] = useState<string | null>(
    null
  )
  const {
    billPeriodsData,
    billPeriodsIsError,
    billPeriodsIsFetching,
    billPeriodsIsLoading,
  } = useAllBillPeriods({
    apiQueryOptions: {
      is_interval_derived: false,
      resource_type: resourceType,
      site_id: siteId,
      // `id` breaks ties so offset paging cannot repeat or skip a row.
      sort: "start_date:asc,id:asc",
      start_date_from: startOfMonthOneYearBefore(start),
      start_date_to: end,
      ...(meterId ? { meter_id: Number(meterId) } : {}),
    },
    organizationId,
  })
  const meterCodeById = new Map(
    meters.map((meter) => [String(meter.id), meter.meterCode])
  )
  const model =
    billPeriodsData == null
      ? null
      : buildSiteBillsMonthlyChart({
          bills: billPeriodsData.map((period) =>
            toChartBill(period, meterCodeById)
          ),
          measure,
          rangeEnd: end,
          rangeStart: start,
        })
  const yAxisLabel =
    model?.yAxisLabel ??
    (measure === "cost" ? t("bills.chart.cost") : t("bills.chart.usage"))
  const showSkeleton =
    !billPeriodsIsError &&
    (billPeriodsIsLoading || (billPeriodsIsFetching && model == null))
  const resolvedSelectedSeriesKey = resolveSelectedSeriesKey(
    showSkeleton,
    selectedSeriesKey,
    billPeriodsIsError ? [] : (model?.series ?? [])
  )
  // Persist a cleared key so an empty or failed chart does not restore it.
  if (resolvedSelectedSeriesKey !== selectedSeriesKey) {
    setSelectedSeriesKey(resolvedSelectedSeriesKey)
  }
  const title = t("bills.chart.title")

  return (
    <GraphMenuContainer
      ariaLabel={t("actionsMenuAriaLabel", {
        ns: "common",
        title,
      })}
      captureElement
      captureTestId="site-bills-monthly-chart-capture"
      fileName={`${title} ${siteId} ${start} to ${end}`}
      isExportDisabled={showSkeleton || billPeriodsIsError || model == null}
      title={title}
    >
      {showSkeleton ? (
        <Skeleton
          aria-label={t("loadingAriaLabel", {
            ns: "common",
            title,
          })}
          height={CHART_HEIGHT}
          variant="rounded"
        />
      ) : null}
      {billPeriodsIsError ? (
        <EmptyChart
          header={t("bills.chart.error.title")}
          subHeader={t("bills.grid.loadError.description")}
        />
      ) : null}
      {!showSkeleton && !billPeriodsIsError && model ? (
        <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
          <HStack
            sx={{
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
              minWidth: 0,
              width: "100%",
            }}
          >
            <SiteBillsChartMeasureToggle
              measure={measure}
              onMeasureChange={setMeasure}
            />
          </HStack>
          <Box
            aria-label={t("bills.chart.ariaLabel")}
            sx={{ height: CHART_HEIGHT, minWidth: 0 }}
          >
            <StackedBarGraph
              customTooltip={
                <SiteBillsMonthlyChartTooltip
                  detailsByMonth={model.detailsByMonth}
                />
              }
              data={model.data}
              formatBucketLabel={(value) =>
                formatBillMonthLabel(value, i18n.language)
              }
              onSelectedSeriesKeyChange={setSelectedSeriesKey}
              selectedSeriesKey={resolvedSelectedSeriesKey}
              series={model.series}
              showVerticalGrid={false}
              tickFormatter={formatDecimal}
              yAxisLabel={yAxisLabel}
            />
          </Box>
        </Stack>
      ) : null}
    </GraphMenuContainer>
  )
}
